import { CONNECT_PROBLEM_CATALOG, CONNECT_PROBLEM_VERSION, normalizeConnectProblem } from "@mdbase-dev/connect-protocol";
/** Internal carrier used while adapting throwing transports into public outcomes. */
export class MdbaseConnectError extends Error {
    problem;
    code;
    status;
    constructor(problem, context = {}) {
        super(problem.message, context.cause === undefined ? undefined : { cause: context.cause });
        this.problem = problem;
        this.name = "MdbaseConnectError";
        this.code = problem.code;
        this.status = context.status;
    }
    get retryable() {
        return this.problem.recovery === "retry"
            && this.problem.operation_outcome !== "unknown";
    }
    get requiresAuthorization() {
        return this.problem.category === "authorization";
    }
    get outcomeUnknown() {
        return this.problem.operation_outcome === "unknown";
    }
    get recovery() {
        return this.problem.recovery;
    }
    get details() {
        return this.problem.details;
    }
}
export function connectProblem(code, message, ...[options = {}]) {
    const definition = CONNECT_PROBLEM_CATALOG[code];
    return {
        problem_version: CONNECT_PROBLEM_VERSION,
        code,
        category: definition.category,
        recovery: definition.recovery,
        message,
        ...(options.details === undefined ? {} : { details: options.details }),
        ...(options.operationOutcome === undefined
            ? {}
            : { operation_outcome: options.operationOutcome }),
        ...(options.traceId === undefined ? {} : { trace_id: options.traceId })
    };
}
export function unknownConnectProblem(serverCode, message, options = {}) {
    return normalizeConnectProblem(serverCode, message, {
        details: options.details,
        operation_outcome: options.operationOutcome,
        trace_id: options.traceId
    });
}
export function connectError(code, message, ...args) {
    const options = args[0];
    return new MdbaseConnectError(connectProblem(code, message, ...args), {
        status: options?.status,
        cause: options?.cause
    });
}
export function serverConnectError(serverCode, message, options = {}) {
    const { status, cause, ...problemOptions } = options;
    return new MdbaseConnectError(normalizeConnectProblem(serverCode, message, {
        details: problemOptions.details,
        operation_outcome: problemOptions.operationOutcome,
        trace_id: problemOptions.traceId
    }), { status, cause });
}
export function operationProblem(envelope) {
    const code = diagnosticProblemCode(envelope.diagnostics);
    const message = diagnosticMessage(envelope.diagnostics);
    if (code === "collection_version_unsupported") {
        const versions = diagnosticVersions(envelope.diagnostics);
        return connectProblem(code, message, {
            operationOutcome: "rejected",
            details: versions
        });
    }
    if (code !== "operation_invalid") {
        return connectProblem(code, message, {
            operationOutcome: "rejected",
            details: { diagnostics: envelope.diagnostics }
        });
    }
    return connectProblem(code, message, {
        operationOutcome: "rejected",
        details: {
            diagnostics: envelope.diagnostics,
            partial_result: envelope.result
        }
    });
}
function diagnosticProblemCode(diagnostics) {
    const codes = new Set(diagnostics
        .filter((diagnostic) => diagnostic.severity === "error")
        .map((diagnostic) => diagnostic.code));
    if (["migration_required", "migration_lossy", "unsupported_version"]
        .some((code) => codes.has(code)))
        return "collection_version_unsupported";
    if (["invalid_config", "invalid_configuration", "missing_config", "missing_spec_version"]
        .some((code) => codes.has(code)))
        return "collection_configuration_invalid";
    if ([
        "duplicate_type",
        "invalid_contract_definition",
        "invalid_data_contract",
        "invalid_type_definition",
        "invalid_type_path"
    ].some((code) => codes.has(code)))
        return "collection_type_registry_invalid";
    if (["collection_invalid", "collection_open_failed"]
        .some((code) => codes.has(code)))
        return "collection_invalid";
    return "operation_invalid";
}
function diagnosticVersions(diagnostics) {
    const source = diagnostics
        .map((diagnostic) => diagnostic.details)
        .find((details) => isRecord(details));
    return {
        current_version: typeof source?.current_version === "string"
            ? source.current_version
            : "0.2.x",
        required_version: typeof source?.required_version === "string"
            ? source.required_version
            : "0.3.0"
    };
}
function isRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}
/** True only when repeating an operation is safe without asking the user. */
export function isRetryableConnectError(error) {
    return error instanceof MdbaseConnectError && error.retryable;
}
function diagnosticMessage(diagnostics) {
    return diagnostics
        .filter((item) => item.severity === "error")
        .map((item) => item.message)
        .join(" ")
        || diagnostics.map((item) => item.message).join(" ")
        || "The collection rejected this operation.";
}
